import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import api from '../lib/api';
import { useNavigate } from 'react-router-dom';

const AlertContext = createContext(null);

// Resolve backend URL. Mirrors logic in lib/api.js — see that file for
// the rationale (production bundle should call same-origin, not a
// build-time-baked URL that may point at a stale preview host).
function _resolveAlertBackendUrl() {
  const envUrl = (process.env.REACT_APP_BACKEND_URL || '').trim();
  if (typeof window === 'undefined' || !window.location || !window.location.origin) {
    return envUrl;
  }
  if (process.env.NODE_ENV !== 'production') {
    return envUrl || window.location.origin;
  }
  return window.location.origin;
}
const API_BASE = _resolveAlertBackendUrl() + '/api';

export function AlertProvider({ children }) {
  const [alerts, setAlerts] = useState([]);
  const [newAlerts, setNewAlerts] = useState([]);
  const [sseConnected, setSseConnected] = useState(false);
  const [pushPermission, setPushPermission] = useState('default');
  const [soundEnabled, setSoundEnabled] = useState(() => {
    try { return localStorage.getItem('eva_alert_sound') !== 'off'; } catch (e) { console.warn('[Alert] localStorage read failed:', e); return true; }
  });

  const sseRef = useRef(null);
  const pollTimerRef = useRef(null);
  const seenIdsRef = useRef(new Set());
  const latestTsRef = useRef('');
  const audioRef = useRef(null);
  const mountedRef = useRef(true);

  // Register service worker on mount
  useEffect(() => {
    mountedRef.current = true;
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js')
        .then((reg) => console.log('[Push] SW registered, scope:', reg.scope))
        .catch((err) => console.error('[Push] SW registration FAILED:', err));
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data?.type === 'NOTIFICATION_CLICK' && event.data.url) {
          window.location.href = event.data.url;
        }
      });
    }
    // Check actual push subscription status, not just permission
    (async () => {
      if ('Notification' in window) {
        const perm = Notification.permission;
        if (perm === 'granted' && 'serviceWorker' in navigator && 'PushManager' in window) {
          try {
            const reg = await navigator.serviceWorker.ready;
            const sub = await reg.pushManager.getSubscription();
            setPushPermission(sub ? 'granted' : 'default');
          } catch (e) {
            console.warn('[Push] Subscription check failed:', e);
            setPushPermission(perm);
          }
        } else {
          setPushPermission(perm);
        }
      }
    })();
    // Notification sound
    try { audioRef.current = new Audio('/notification.mp3'); } catch (e) { console.warn('[Alert] Audio init failed:', e); }
    return () => { mountedRef.current = false; };
  }, []);

  const playSound = useCallback(() => {
    if (soundEnabled && audioRef.current) {
      audioRef.current.play().catch(() => {});
    }
  }, [soundEnabled]);

  // Process incoming alert: dedup, add to newAlerts + alerts
  const processAlert = useCallback((alert) => {
    const id = alert.id;
    if (!id || seenIdsRef.current.has(id)) return;
    seenIdsRef.current.add(id);
    if (seenIdsRef.current.size > 500) {
      const arr = [...seenIdsRef.current];
      seenIdsRef.current = new Set(arr.slice(-250));
    }

    if (!mountedRef.current) return;
    setNewAlerts((prev) => [alert, ...prev]);
    setAlerts((prev) => [alert, ...prev].slice(0, 100));
    playSound();
  }, [playSound]);

  // Polling fallback. Keep this callback stable so SSE failures can
  // switch transport without capturing a stale function from an older render.
  const startPolling = useCallback(() => {
    if (pollTimerRef.current) return;
    pollTimerRef.current = setInterval(async () => {
      try {
        const since = latestTsRef.current || '';
        const params = since ? `?since=${encodeURIComponent(since)}` : '?limit=20';
        const resp = await api.get(`/alerts${params}`);
        const list = resp.data?.alerts || [];
        if (list.length > 0) {
          latestTsRef.current = list[0].created_at;
          list.reverse().forEach(processAlert);
        }
      } catch (err) { console.warn('[Alert] Poll failed:', err); }
    }, 5000);
  }, [processAlert]);

  // SSE connection
  const connectSSE = useCallback(() => {
    const token = localStorage.getItem('eva_token');
    if (!token) return;

    if (sseRef.current) {
      sseRef.current.close();
      sseRef.current = null;
    }

    const url = `${API_BASE}/events/stream?token=${encodeURIComponent(token)}`;
    const es = new EventSource(url);
    sseRef.current = es;

    es.addEventListener('connected', () => {
      if (!mountedRef.current) return;
      setSseConnected(true);
      // SSE active → stop polling
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    });

    const eventTypes = ['booking_created', 'booking_updated', 'booking_cancelled', 'customer_arrival', 'manual_mode_message', 'eva_off_message', 'outbound_delivery_problem'];
    eventTypes.forEach((type) => {
      es.addEventListener(type, (e) => {
        try {
          const data = JSON.parse(e.data);
          processAlert({ id: data.id, type, data: data.data || data, created_at: data.created_at });
        } catch (err) { console.warn('[Alert] SSE parse error:', type, err); }
      });
    });

    es.addEventListener('ping', () => {});

    es.onerror = () => {
      if (!mountedRef.current) return;
      setSseConnected(false);
      es.close();
      sseRef.current = null;
      startPolling();
      setTimeout(() => { if (mountedRef.current) connectSSE(); }, 10000);
    };
  }, [processAlert, startPolling]);

  // Helper: convert VAPID base64url key to Uint8Array
  const urlBase64ToUint8Array = useCallback((base64String) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = window.atob(base64);
    return Uint8Array.from([...raw].map(c => c.charCodeAt(0)));
  }, []);

  // Core: ensure a valid push subscription exists and is synced to backend
  const ensurePushSubscription = useCallback(async (requestPermission = false) => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      console.warn('[Push] APIs not available');
      return false;
    }
    try {
      let permission = Notification.permission;
      if (requestPermission && permission === 'default') {
        permission = await Notification.requestPermission();
      }
      setPushPermission(permission);
      if (permission !== 'granted') return false;

      // Timeout to prevent hanging if SW never activates
      const reg = await Promise.race([
        navigator.serviceWorker.ready,
        new Promise((_, rej) => setTimeout(() => rej(new Error('SW ready timeout (10s)')), 10000)),
      ]);

      let sub = await reg.pushManager.getSubscription();

      if (!sub) {
        console.log('[Push] No existing subscription, creating new...');
        const vapidResp = await api.get('/push/vapid-key');
        const vapidKey = vapidResp.data?.publicKey;
        if (!vapidKey) { console.error('[Push] No VAPID key from backend'); return false; }
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidKey),
        });
        console.log('[Push] New subscription created:', new URL(sub.endpoint).host);
      }

      const subJson = sub.toJSON();
      if (subJson?.endpoint && subJson?.keys) {
        await api.post('/push/subscribe', {
          endpoint: subJson.endpoint,
          keys: subJson.keys,
        });
        console.log('[Push] Subscription synced to backend');
        setPushPermission('granted');
      }
      return true;
    } catch (e) {
      console.error('[Push] Subscribe failed:', e.name, e.message);
      return false;
    }
  }, [urlBase64ToUint8Array]);

  // Public: explicit subscribe (from toggle button — requests permission)
  const subscribePush = useCallback(async () => {
    return ensurePushSubscription(true);
  }, [ensurePushSubscription]);

  const unsubscribePush = useCallback(async () => {
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        const subJson = sub.toJSON();
        await api.post('/push/unsubscribe', {
          endpoint: subJson.endpoint,
          keys: subJson.keys,
        });
        await sub.unsubscribe();
      }
      setPushPermission('default');
    } catch (e) {
      console.error('Push unsubscribe failed:', e);
    }
  }, []);

  // Connect on mount / auth change
  useEffect(() => {
    const token = localStorage.getItem('eva_token');
    if (token) {
      connectSSE();
      startPolling();
      // Auto-sync push subscription if permission already granted
      if ('Notification' in window && Notification.permission === 'granted') {
        ensurePushSubscription(false);
      }
    }
    return () => {
      if (sseRef.current) { sseRef.current.close(); sseRef.current = null; }
      if (pollTimerRef.current) { clearInterval(pollTimerRef.current); pollTimerRef.current = null; }
    };
  }, [connectSSE, startPolling, ensurePushSubscription]);

  // --- Actions matching existing AlertToast interface ---
  const dismissNew = useCallback((alertId) => {
    setNewAlerts((prev) => prev.filter((a) => a.id !== alertId));
  }, []);

  const deleteAlert = useCallback(async (alertId) => {
    setAlerts((prev) => prev.filter((a) => a.id !== alertId));
    setNewAlerts((prev) => prev.filter((a) => a.id !== alertId));
    try { await api.delete(`/alerts/${alertId}`); } catch (e) { console.warn('[Alert] Delete failed:', e); }
  }, []);

  const clearAlerts = useCallback(async () => {
    setAlerts([]);
    setNewAlerts([]);
    try { await api.delete('/alerts'); } catch (e) { console.warn('[Alert] Clear failed:', e); }
  }, []);

  const toggleSound = useCallback(() => {
    setSoundEnabled((prev) => {
      const next = !prev;
      try { localStorage.setItem('eva_alert_sound', next ? 'on' : 'off'); } catch (e) { console.warn('[Alert] localStorage write failed:', e); }
      return next;
    });
  }, []);

  const ctxValue = useMemo(() => ({
    alerts,
    newAlerts,
    sseConnected,
    pushPermission,
    soundEnabled,
    dismissNew,
    deleteAlert,
    clearAlerts,
    toggleSound,
    subscribePush,
    unsubscribePush,
  }), [alerts, newAlerts, sseConnected, pushPermission, soundEnabled, dismissNew, deleteAlert, clearAlerts, toggleSound, subscribePush, unsubscribePush]);

  return (
    <AlertContext.Provider value={ctxValue}>
      {children}
    </AlertContext.Provider>
  );
}

export function useAlerts() {
  const ctx = useContext(AlertContext);
  if (!ctx) throw new Error('useAlerts must be used within AlertProvider');
  return ctx;
}
